/**
 * MomentsMediaUploader — one snippet used by every guest upload flow
 * (public /m/:slug page + owner test-upload button + WordPress shortcode).
 *
 * Native phone features leveraged:
 *   • <input capture="environment"> opens the phone's rear camera
 *     directly. No JS libraries required. Falls back to the file picker
 *     on desktop browsers.
 *   • navigator.geolocation grabs an optional geo-tag when the user
 *     grants permission. Silent if denied — never blocks upload.
 *   • navigator.share is offered post-upload so the guest can share the
 *     event link with their friends via their phone's native share sheet.
 *
 * Owner rule #4: this is a GLOBAL SNIPPET. Do NOT clone it into a page.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera, Video, Loader2, ImagePlus, CheckCircle2, Share2, MapPin,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

const MAX_MB = 25;

export default function MomentsMediaUploader({
  slug,
  accentHex   = "#f59e0b",
  allowVideo  = true,
  allowCaption = true,
  guestUrl    = "",
  onUploaded,
  testid = "moments-uploader",
}) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [uploader, setUploader] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState([]);   // last 8 previews
  const [progress, setProgress] = useState(0);

  const _pickFile = (fromCamera) => {
    if (busy) return;
    const el = fromCamera ? camRef.current : galRef.current;
    if (el) el.click();
  };

  const _handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    // Optional geolocation — silent if denied
    let coords = null;
    try {
      coords = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => res(null),
          { enableHighAccuracy: false, timeout: 3000, maximumAge: 60000 },
        );
      });
    } catch (_e) { /* ignore */ }

    setBusy(true);
    setProgress(0);

    let done = 0;
    for (const f of files) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} is bigger than ${MAX_MB} MB — skipped`);
        done += 1;
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", f);
        if (caption) form.append("caption", caption);
        if (uploader) form.append("uploader_name", uploader);
        if (coords) {
          form.append("lat", String(coords.lat));
          form.append("lng", String(coords.lng));
        }
        const r = await fetch(`${API}/api/moments/m/${slug}/upload`, {
          method: "POST", body: form,
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        setUploaded((u) => [{
          media_id:   j.media_id,
          public_url: j.public_url,
          kind:       j.kind,
        }, ...u].slice(0, 8));
        onUploaded?.(j);
      } catch (e) {
        toast.error(`Upload failed: ${e.message || e}`);
      }
      done += 1;
      setProgress(Math.round((done / files.length) * 100));
    }

    setBusy(false);
    setProgress(0);
    setCaption("");
    toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded — thank you!`);
  };

  const _share = async () => {
    const shareData = {
      title: "Add your photos & videos",
      text:  "Share the moments you captured — scan or tap:",
      url:   guestUrl || (typeof window !== "undefined" ? window.location.href : ""),
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard?.writeText(shareData.url);
        toast.success("Link copied — share it anywhere");
      }
    } catch (_e) { /* user cancelled */ }
  };

  const accept = allowVideo
    ? "image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
    : "image/jpeg,image/png,image/webp,image/heic,image/heif";

  return (
    <div className="hp-moments-uploader space-y-4" data-testid={`${testid}-root`}>
      {/* Hidden native inputs */}
      <input
        ref={camRef}
        type="file"
        accept={accept}
        capture="environment"
        multiple
        hidden
        data-testid={`${testid}-camera-input`}
        onChange={(e) => _handleFiles(e.target.files)}
      />
      <input
        ref={galRef}
        type="file"
        accept={accept}
        multiple
        hidden
        data-testid={`${testid}-gallery-input`}
        onChange={(e) => _handleFiles(e.target.files)}
      />

      {/* Big native-first CTAs */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => _pickFile(true)}
          disabled={busy}
          data-testid={`${testid}-camera-btn`}
          className="flex flex-col items-center justify-center gap-2 h-24 rounded-2xl border-2 border-dashed border-stone-300 bg-white hover:border-current transition disabled:opacity-60"
          style={{ color: accentHex }}
        >
          <Camera size={26}/>
          <span className="text-sm font-semibold text-stone-800">Take a photo</span>
        </button>
        <button
          type="button"
          onClick={() => _pickFile(false)}
          disabled={busy}
          data-testid={`${testid}-gallery-btn`}
          className="flex flex-col items-center justify-center gap-2 h-24 rounded-2xl border-2 border-dashed border-stone-300 bg-white hover:border-current transition disabled:opacity-60"
          style={{ color: accentHex }}
        >
          <ImagePlus size={26}/>
          <span className="text-sm font-semibold text-stone-800">From gallery</span>
        </button>
      </div>

      {/* Optional fields */}
      <input
        type="text"
        value={uploader}
        onChange={(e) => setUploader(e.target.value.slice(0, 60))}
        placeholder="Your name (optional)"
        maxLength={60}
        data-testid={`${testid}-name`}
        className="w-full h-11 rounded-xl border border-stone-300 px-3 text-[15px] bg-white"
      />
      {allowCaption && (
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 280))}
          placeholder="Add a short caption (optional)"
          rows={2}
          maxLength={280}
          data-testid={`${testid}-caption`}
          className="w-full rounded-xl border border-stone-300 px-3 py-2 text-[15px] bg-white resize-none"
        />
      )}

      {/* Progress */}
      {busy && (
        <div className="rounded-xl bg-stone-100 p-3 flex items-center gap-3"
             data-testid={`${testid}-progress`}>
          <Loader2 className="animate-spin" size={18}/>
          <div className="flex-1 h-1.5 rounded-full bg-stone-200 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${progress}%`, background: accentHex }}
            />
          </div>
          <span className="text-xs text-stone-600 tabular-nums">{progress}%</span>
        </div>
      )}

      {/* Just-uploaded preview strip */}
      {uploaded.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid={`${testid}-thumbs`}>
          {uploaded.map((u) => (
            <div key={u.media_id}
                 className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden ring-1 ring-stone-200">
              {u.kind === "video" ? (
                <video src={u.public_url} className="w-full h-full object-cover" muted playsInline/>
              ) : (
                <img src={u.public_url} alt="" className="w-full h-full object-cover"/>
              )}
              <div className="absolute bottom-1 right-1 rounded-full bg-white/90 p-0.5">
                <CheckCircle2 size={14} className="text-emerald-600"/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Share back to friends — native share sheet */}
      <div className="flex items-center gap-2 pt-2 border-t border-stone-200">
        <button
          type="button"
          onClick={_share}
          data-testid={`${testid}-share`}
          className="inline-flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900"
        >
          <Share2 size={16}/> Share this page with friends
        </button>
        {allowVideo && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-stone-500">
            <Video size={13}/> Video OK
          </span>
        )}
      </div>

      <p className="text-[11px] text-stone-400 flex items-center gap-1">
        <MapPin size={11}/> If you allow location, we tag your photo with where you took it.
      </p>
    </div>
  );
}
